import { useState } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import PaymentIcon from "@mui/icons-material/Payment";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalShippingIcon from "@mui/icons-material/LocalShipping"; // Icon for Suppliers
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"; // Icon for Cash Flow
import BadgeIcon from "@mui/icons-material/Badge"; // Icon for Employees
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner"; // Icon for Inventory Action Page

import { useUser } from '../contexts/UserContext'; 

const SidebarComponent = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  
  // Get the list of allowed pages from userProfile
  const allowedPages = userProfile?.ui_config?.allowed_pages || [];

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 

  const productLabel = generalSchema.product?.label || 'מוצר';
  const productPluralLabel = generalSchema.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema.new_order?.label || 'הזמנה חדשה';

  // Function to check if user has access to a specific page
  const hasPageAccess = (pageName) => {
    // Define pages that should always be accessible (default pages)
    const alwaysAllowedPages = [
      'DashboardPage',
      'SuppliersPage',
      'SupplierDetailPage',
      'ClientsListPageCached',
      'ClientDetailPage',
      'ChangePasswordPage',
      'UpdateProfilePage',
      'MinimalPrintPage',
      'CashFlowPage',
    ];
    
    // Check if it's a default page that should always be allowed
    const isDefaultPage = alwaysAllowedPages.includes(pageName);
    
    // First, check if the page is a default page OR in the allowed_pages list
    if (!isDefaultPage && !allowedPages.includes(pageName)) {
      return false;
    }
    
    // Default fallback roles if config not available
    const defaultRoles = {
      'DashboardPage': ['manager', 'employee'],
      'ClientsListPageCached': ['manager', 'employee'],
      'DeletedClientsPage': ['manager'],
      'MinimalPrintPage': ['manager', 'employee'],
      'ClientDetailPage': ['manager', 'employee'],
      'GroupsManagementPage': ['manager'],
      'GroupDetailPage': ['manager'],
      'MinimalGroupPrintPage': ['manager'],
      'TransactionsPage': ['manager'],
      'FunctionalGroupsPage': ['manager'],
      'YearlyFeeManagementPage': ['manager'],
      'SuppliersPage': ['manager', 'employee'],
      'CashFlowPage': ['manager']
    };

    // If no profile or no config, use defaults
    if (!userProfile || !userProfile.ui_config || !userProfile.ui_config.pages_configs) {
      return defaultRoles[pageName]?.includes(role) || false;
    }

    // Get page config
    const pageConfig = userProfile.ui_config.pages_configs[pageName];
    
    // If page config exists and has allowed_roles, use them
    if (pageConfig && pageConfig.allowed_roles) {
      return pageConfig.allowed_roles.includes(role);
    }
    
    // Fallback to defaults
    return defaultRoles[pageName]?.includes(role) || false;
  };

  const sidebarStyles = {
    height: "100vh", // full page height
    width: "auto",   // let the container auto-size
    fontWeight: "bold !important",
    fontSize: "1.5rem !important",

    "& .pro-sidebar-inner": {
      background: "#1e293b",
    },
    "& .pro-icon-wrapper": {
      backgroundColor: "transparent !important",
    },
    "& .pro-inner-item": {
      padding: "5px 35px 5px 20px !important",
    },
    "& .pro-inner-item:hover": {
      color: "#868dfb !important",
    },
    "& .pro-menu-item.active": {
      color: "#6870fa !important",
    },
  };

  return (
    <Box sx={sidebarStyles}>
      {/* Only change: width="auto" so expanded fits content */}
      <Sidebar collapsed={isCollapsed} width="auto">
        <Menu iconShape="square" sx={{fontWeight: "bold"}}>
          {/* Toggle Sidebar */}
          <MenuItem
            icon={<MenuOutlinedIcon />}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {!isCollapsed && <Typography>תפריט</Typography>}
          </MenuItem>

          {/* Navigation Items */}
          {hasPageAccess('DashboardPage') && (
            <MenuItem icon={<DashboardIcon />} onClick={() => navigate("/dashboard")}>
              {!isCollapsed && <Typography>כללי</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('ClientsListPageCached') && (
            <MenuItem icon={<BusinessIcon />} onClick={() => navigate("/clients")}>
              {!isCollapsed && <Typography>לקוחות</Typography>}
            </MenuItem>
          )}

         {hasPageAccess('SuppliersPage') && (
            <MenuItem icon={<LocalShippingIcon />} onClick={() => navigate("/suppliers")}>
              {!isCollapsed && <Typography>ספקים</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('TransactionsPage') && (
            <MenuItem icon={<PaymentIcon />} onClick={() => navigate("/transactions")}>
              {!isCollapsed && <Typography>דוח תנועות</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('CashFlowPage') && (
            <MenuItem icon={<AccountBalanceIcon />} onClick={() => navigate("/cashflow")}>
              {!isCollapsed && <Typography>תזרים מזומנים</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('EmployeesPage') && (
            <MenuItem icon={<BadgeIcon />} onClick={() => navigate("/employees")}>
              {!isCollapsed && <Typography>עובדים</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('GroupsManagementPage') && (
            <MenuItem icon={<GroupIcon />} onClick={() => navigate("/groups")}>
              {!isCollapsed && <Typography>ניהול קבוצות</Typography>}
            </MenuItem>
          )}

          {hasPageAccess('FunctionalGroupsPage') && (
            <MenuItem icon={<GroupWorkIcon />} onClick={() => navigate("/functional-groups")}>
              {!isCollapsed && <Typography>פעילות קבוצתית</Typography>}
            </MenuItem>
          )}

          {/* Add YearlyFeeManagementPage menu item */}
          {hasPageAccess('YearlyFeeManagementPage') && (
            <MenuItem icon={<MonetizationOnIcon />} onClick={() => navigate("/yearly-fee")}>
              {!isCollapsed && <Typography>ניהול שכר טרחה</Typography>}
            </MenuItem>
          )}


          {hasPageAccess('ProductsPage') && (
            isCollapsed ? (
              <MenuItem icon={<ShoppingCartIcon />} onClick={() => navigate("/products")} />
            ) : hasPageAccess('InventoryActionPage') ? (
              <SubMenu icon={<ShoppingCartIcon />} label={<Typography>{productPluralLabel}</Typography>}>
                <MenuItem onClick={() => navigate("/products")}>
                  <Typography>{productPluralLabel}</Typography>
                </MenuItem>
                <MenuItem onClick={() => navigate("/inventory-action")}>
                  <Typography>פעולת מלאי</Typography>
                </MenuItem>
              </SubMenu>
            ) : (
              <MenuItem icon={<ShoppingCartIcon />} onClick={() => navigate("/products")}>
                <Typography>{productPluralLabel}</Typography>
              </MenuItem>
            )
          )}

          {hasPageAccess('OrdersPage') && (
            isCollapsed ? (
              <MenuItem icon={<ReceiptIcon />} onClick={() => navigate("/orders")} />
            ) : (
              <SubMenu icon={<ReceiptIcon />} label={<Typography>{orderPluralLabel}</Typography>}>
                <MenuItem onClick={() => navigate("/order/new")}>
                  <Typography>{newOrderLabel}</Typography>
                </MenuItem>
                <MenuItem onClick={() => navigate("/orders")}>
                  <Typography>{orderPluralLabel}</Typography>
                </MenuItem>
                <MenuItem onClick={() => navigate("/quotations")}>
                  <Typography>הצעות מחיר</Typography>
                </MenuItem>
              </SubMenu>
            )
          )}

          {hasPageAccess('DeletedClientsPage') && (
            <MenuItem icon={<DeleteSweepIcon />} onClick={() => navigate("/clients/deleted")}>
              {!isCollapsed && <Typography>לקוחות שנמחקו</Typography>}
            </MenuItem>
          )}

        </Menu>
      </Sidebar>
    </Box>
  );
};

export default SidebarComponent;